import { useEffect, useState, useRef } from "react";
import { ArrowRight, ChevronDown, Coins, ShieldCheck, Target, Check, Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { journeyApi } from "@/lib/journeyApi";
import { JourneyFooter, BrandMark } from "@/components/JourneyChrome";

// =====================================================================
// LOCKED PUBLIC SALES COPY - sole source of truth:
// /app/memory/LOCKED_Sales_Page_Copy_FINAL.md (14 sections, verbatim).
// Authorized exceptions: public purchase CTA = "GET THE PAYCHECK EDIT"
// (founder override, supersedes the locked-copy "MAKE AN EDIT" CTA exception;
// "Make an Edit" remains only as editorial rhythm copy); onboarding FAQ from
// verified behavior. Visual system = founder-accepted iteration 56/57.
// Display headings use the accepted serif treatment; WORDS are verbatim.
// =====================================================================

const FAQS = [
  {
    q: "Is this another budgeting app?",
    a: "No. The Paycheck Edit is centered on helping you evaluate what your available money can do next, not continuously tracking transactions or maintaining a detailed category budget.",
  },
  {
    q: "Is this basically a calculator?",
    a: "No. Calculations are part of the experience, but arithmetic isn't the problem The Paycheck Edit is designed to solve. It reduces the repeated work of reconstructing the relevant financial picture, working through competing priorities and deciding what your available money can do next.",
  },
  {
    q: "Couldn't I build this myself?",
    a: "Potentially. The founder originally worked through the underlying approach manually. The fact that something can be done manually doesn't mean you want to keep doing it manually.",
  },
  {
    q: "Will this become another system I have to constantly maintain?",
    a: "The Paycheck Edit isn't built around continuously monitoring transactions or daily category maintenance. You provide the financial information needed to calculate your Edit and update relevant information when reality changes.",
  },
  {
    q: "Does it connect to or move money from my bank?",
    a: "No. The Paycheck Edit does not connect to or move money from your bank. You provide the information the system uses.",
    bold: "The software calculates. You decide.",
  },
  {
    q: "Is this only useful while I'm paying off debt?",
    a: "No. Debt may be part of your financial picture, but the broader system is built around PROTECT · ELIMINATE · BUILD · FREEDOM so it can remain relevant as your priorities change.",
  },
];

const PRACTICAL = [
  {
    q: "Why $147?",
    a: "Because you're not paying $147 for a calculator. You're paying for a system designed to reduce the financial admin you keep having to do yourself. You get paid again. Circumstances change again. Priorities compete again. The decision isn't a one-time problem. The Paycheck Edit is a one-time purchase.",
  },
  {
    // Authorized exception: onboarding answer from verified behavior only.
    q: "How difficult is it to get started?",
    a: "The Paycheck Edit uses a guided, self-entered setup. You provide the financial information the system needs without connecting a bank account, establish your Foundation, and then move into your first Edit. Your saved information supports future Edits, and you can update relevant details when your circumstances change.",
  },
  {
    q: "What if my circumstances change?",
    a: "The Paycheck Edit includes ways to account for changes, reconsider a move, pause when needed and return when you're ready. The goal is to work with real financial life, not pretend nothing ever changes.",
  },
  {
    q: "What if there isn't money available for another Edit?",
    a: "That's okay. The Paycheck Edit™ will not force an allocation when the information you entered does not show money available for another Edit. You can review your current information, leave eligible money unassigned, or return when your situation changes.",
  },
  {
    q: "Is this financial advice?",
    a: "The Paycheck Edit™ provides educational financial planning tools and illustrative calculations based on the information you enter. It does not provide individualized financial, investment, tax, legal, accounting, credit-repair, or other professional advice. You review the calculations and decide whether to act.",
  },
  {
    q: "Do I have to connect my bank account?",
    a: "No. The Paycheck Edit™ does not require bank connections, account numbers, financial logins, or transaction history. You enter the information used for your calculations, and you decide what to do with the results.",
  },
  {
    q: "Is this a subscription?",
    a: "No. The Paycheck Edit™ is a $147 one-time purchase with no mandatory monthly subscription.",
  },
  {
    q: "What about access, refunds and other purchase terms?",
    a: (
      <>
        Your purchase includes digital access after successful checkout and account activation. The Paycheck Edit™ includes a 14-Day Money-Back Guarantee. The 14-day refund period begins when your payment is successfully charged and completed by the payment processor. See the{" "}
        <Link to="/legal#refund" data-testid="faq-refund-link" className="font-medium text-[#2F6B57] underline hover:text-[#164C3B]">14-Day Refund Policy</Link>,{" "}
        <Link to="/legal#terms" data-testid="faq-terms-link" className="font-medium text-[#2F6B57] underline hover:text-[#164C3B]">Terms</Link>,{" "}
        <Link to="/legal#privacy" data-testid="faq-privacy-link" className="font-medium text-[#2F6B57] underline hover:text-[#164C3B]">Privacy Policy</Link>, and{" "}
        <Link to="/legal#disclaimer" data-testid="faq-disclaimer-link" className="font-medium text-[#2F6B57] underline hover:text-[#164C3B]">Financial Disclaimer</Link>{" "}
        for full details.
      </>
    ),
  },
  {
    q: "Where is The Paycheck Edit™ available?",
    a: "The Paycheck Edit™ is currently available to customers in the United States only.",
  },
];

export default function SalesPage() {
  const navigate = useNavigate();
  const [busy] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);
  const [navOpen, setNavOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  // Warm the payments config (price + publishable key) for the branded checkout.
  useEffect(() => { journeyApi.paymentsConfig().catch(() => {}); }, []);

  // Mobile floating CTA appears only after the visitor scrolls past the hero.
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > window.innerHeight * 0.9);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile nav sheet is open.
  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [navOpen]);

  // Every purchase CTA routes into the branded in-app embedded checkout (/checkout),
  // where Stripe renders its payment UI inside The Paycheck Edit experience.
  const startCheckout = () => navigate("/checkout");

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const GetTheEdit = ({ testid, tone = "light", size = "lg", full = false }) => {
    const base = "group inline-flex items-center justify-center gap-2.5 rounded-full font-semibold uppercase tracking-[0.05em] transition-all duration-200 disabled:opacity-60";
    const sz = size === "sm" ? "min-h-[2.75rem] px-5 text-[13px]" : "min-h-[3.5rem] px-6 text-[15px] sm:px-9 sm:text-base";
    const toneCls = tone === "onDark"
      ? "bg-white text-[#164C3B] shadow-[0_16px_40px_-18px_rgba(0,0,0,0.45)] hover:bg-[#F3ECDD]"
      : "bg-[#164C3B] text-white shadow-[0_16px_40px_-18px_rgba(22,76,59,0.6)] hover:bg-[#0f3528]";
    return (
      <button type="button" onClick={startCheckout} disabled={busy} data-testid={testid} className={`${base} ${sz} ${full ? "w-full" : ""} ${toneCls}`}>
        {busy ? "Opening secure checkout…" : "GET THE PAYCHECK EDIT"}
        {!busy && <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />}
      </button>
    );
  };

  // Premium contained purchase "bubble" — used for every major CTA so buying never blends into body copy.
  const OfferCard = ({ testid, tone = "light", caption, priceLabel = "$147 · ONE-TIME PURCHASE", note = "No subscription. No bank connection required.", children, align = "center" }) => {
    const dark = tone === "dark";
    const shell = dark
      ? "border-[#2F6B57]/45 bg-[#103B2D] shadow-[0_28px_70px_-34px_rgba(0,0,0,0.6)]"
      : "border-[#E7E0D3] bg-[#FCFBF8] shadow-[0_26px_64px_-34px_rgba(22,76,59,0.3)]";
    const alignCls = align === "left" ? "items-start text-left" : "items-center text-center";
    return (
      <div data-testid={`${testid}-card`} className={`flex w-full max-w-[560px] flex-col gap-5 rounded-[1.75rem] border p-7 sm:p-9 ${shell} ${alignCls} ${align === "center" ? "mx-auto" : ""}`}>
        {caption && <p className={`pe-eyebrow ${dark ? "!text-[#C9A86A]" : "text-[#2F6B57]"}`}>{caption}</p>}
        {children}
        <GetTheEdit testid={testid} tone={dark ? "onDark" : "light"} full />
        <div>
          <p className={`text-sm font-semibold tracking-wide ${dark ? "text-[#C9A86A]" : "text-[#164C3B]"}`}>{priceLabel}</p>
          <p className={`mt-1 text-sm ${dark ? "text-[#9FBBAE]" : "text-[#70584A]"}`}>{note}</p>
        </div>
      </div>
    );
  };

  const OfferBand = ({ testid, bg = "border-t border-[#ECE6DC] bg-white" }) => (
    <section className={bg} data-testid={`${testid}-band`}>
      <div className="mx-auto w-full max-w-[920px] px-5 py-14 sm:px-8">
        <OfferCard testid={testid} />
      </div>
    </section>
  );

  const NAV_ANCHORS = [
    ["How It Works", "how-it-works"],
    ["The Receipts", "receipts"],
    ["About", "about"],
    ["FAQ", "faq"],
  ];
  const goAnchor = (id) => { setNavOpen(false); setTimeout(() => scrollTo(id), navOpen ? 180 : 0); };

  const SalesNav = () => (
    <>
      <header className="sticky top-0 z-40 border-b border-[#E5E0D9] bg-[#FAF8F4]/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-[1120px] items-center justify-between px-5 sm:px-8">
          <BrandMark />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_ANCHORS.map(([label, id]) => (
              <button key={id} type="button" onClick={() => goAnchor(id)} data-testid={`nav-${id}`}
                className="text-sm font-medium text-[#3f3f3f] transition-colors hover:text-[#164C3B]">{label}</button>
            ))}
            <Link to="/login" data-testid="nav-signin" className="text-sm font-medium text-[#164C3B] transition-colors hover:text-[#2F6B57]">Sign in</Link>
            <GetTheEdit testid="nav-purchase-button" size="sm" />
          </nav>
          <button type="button" onClick={() => setNavOpen(true)} data-testid="nav-hamburger" aria-label="Open menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full pe-surface text-[#164C3B] lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {navOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-[#FAF8F4] lg:hidden" data-testid="nav-mobile-sheet">
          <div className="flex h-16 items-center justify-between border-b border-[#E5E0D9] px-5 sm:px-8">
            <BrandMark />
            <button type="button" onClick={() => setNavOpen(false)} data-testid="nav-close" aria-label="Close menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full pe-surface text-[#164C3B]">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto px-5 py-6 sm:px-8">
            {NAV_ANCHORS.map(([label, id]) => (
              <button key={id} type="button" onClick={() => goAnchor(id)} data-testid={`nav-m-${id}`}
                className="w-full border-b border-[#ECE6DC] py-4 text-left font-serif text-2xl text-[#171717]">{label}</button>
            ))}
            <Link to="/login" onClick={() => setNavOpen(false)} data-testid="nav-m-signin"
              className="w-full border-b border-[#ECE6DC] py-4 text-left font-serif text-2xl text-[#171717]">Sign in / Activate</Link>
            <a href="mailto:hello@thepaycheckedit.co" data-testid="nav-m-contact"
              className="w-full border-b border-[#ECE6DC] py-4 text-left text-base font-medium text-[#3f3f3f]">Contact Us</a>
            <Link to="/legal#support" onClick={() => setNavOpen(false)} data-testid="nav-m-support"
              className="w-full border-b border-[#ECE6DC] py-4 text-left text-base font-medium text-[#3f3f3f]">Support / Help</Link>
            <Link to="/legal#terms" onClick={() => setNavOpen(false)} data-testid="nav-m-terms"
              className="w-full border-b border-[#ECE6DC] py-4 text-left text-base font-medium text-[#3f3f3f]">Terms</Link>
            <Link to="/legal#privacy" onClick={() => setNavOpen(false)} data-testid="nav-m-privacy"
              className="w-full py-4 text-left text-base font-medium text-[#3f3f3f]">Privacy</Link>
          </div>
          <div className="border-t border-[#E5E0D9] px-5 py-5 sm:px-8">
            <button type="button" onClick={() => { setNavOpen(false); startCheckout(); }} disabled={busy} data-testid="nav-m-purchase-button"
              className="group flex min-h-[3.25rem] w-full items-center justify-center gap-2.5 rounded-full bg-[#164C3B] px-6 text-base font-semibold uppercase tracking-[0.05em] text-white transition-all duration-200 hover:bg-[#0f3528] disabled:opacity-60">
              {busy ? "Opening secure checkout…" : "GET THE PAYCHECK EDIT"}
              {!busy && <ArrowRight className="h-5 w-5" />}
            </button>
            <p className="mt-2 text-center text-sm font-semibold tracking-wide text-[#164C3B]">$147 · ONE-TIME PURCHASE</p>
            <p className="text-center text-xs text-[#70584A]">No subscription. No bank connection required.</p>
          </div>
        </div>
      )}
    </>
  );

  const StickyCTA = () => {
    const visible = showSticky && !navOpen;
    return (
      <div data-testid="sticky-cta" aria-hidden={!visible}
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-[#E5E0D9] bg-[#FAF8F4]/95 px-4 py-3 backdrop-blur-md transition-all duration-300 lg:hidden ${visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0"}`}
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <button type="button" onClick={startCheckout} disabled={busy} tabIndex={visible ? 0 : -1} data-testid="sticky-purchase-button"
          className="group flex min-h-[3rem] w-full items-center justify-center gap-2 rounded-full bg-[#164C3B] px-6 text-[15px] font-semibold uppercase tracking-[0.04em] text-white transition-all duration-200 hover:bg-[#0f3528] disabled:opacity-60">
          {busy ? "Opening secure checkout…" : "GET THE PAYCHECK EDIT · $147"}
          {!busy && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    );
  };

  // shared type helpers (visual only)
  const H = "font-serif leading-[1.08] tracking-tight text-[#171717]";
  const Hd = "font-serif leading-[1.08] tracking-tight text-white";
  const body = "text-lg leading-relaxed text-[#3f3f3f]";
  const bodyLight = "text-lg leading-relaxed text-[#C7D8CE]";

  return (
    <div className="min-h-screen bg-[#FAF8F4] pb-20 text-[#171717] lg:pb-0" data-testid="sales-page">
      <SalesNav />

      {/* SECTION 1 - HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-32 h-96 w-96 rounded-full bg-[#DDE7DF] opacity-50 blur-3xl" />
        <div className="pointer-events-none absolute -left-28 top-72 h-72 w-72 rounded-full bg-[#F3ECDD] opacity-60 blur-3xl" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-start gap-12 px-5 pb-20 pt-14 sm:px-8 lg:grid-cols-[1.04fr_0.96fr] lg:gap-12 lg:pb-28 lg:pt-16">
          <div className="fade-up">
            <p className="text-[11px] font-semibold uppercase leading-snug tracking-[0.2em] text-[#B8923F]">
              For people who make money and still wonder why it doesn't feel like enough is changing.
            </p>
            <h1 className={`mt-6 max-w-[600px] text-[2.1rem] leading-[1.08] sm:text-[2.9rem] lg:text-[3.15rem] ${H}`}>Your money has a lot of jobs.</h1>
            <h1 className={`mt-2 max-w-[600px] text-[2.1rem] leading-[1.08] sm:text-[2.9rem] lg:text-[3.15rem] ${H} !text-[#164C3B]`}>The Paycheck Edit helps you decide what gets what.</h1>
            <p className="mt-4 font-serif text-xl italic text-[#2F6B57] sm:text-2xl">Without rebuilding the math every time something changes.</p>
            <div className={`mt-7 max-w-xl space-y-4 ${body}`}>
              <p>You know how to make money.</p>
              <p>It's everything after the deposit that somehow turns into an administrative position you never applied for.</p>
            </div>
            <div className="mt-6 flex flex-col items-start gap-y-1.5 text-base font-medium text-[#164C3B] sm:flex-row sm:flex-wrap sm:gap-x-6">
              <span>What's due?</span><span>What's already handled?</span><span>What changed?</span>
              <span>What's actually available?</span><span>What can it do?</span>
            </div>
            <p className="mt-7 max-w-xl text-[17px] font-semibold leading-relaxed text-[#171717]">
              The Paycheck Edit™ is a personal money decision system designed to handle more of the calculation behind what your available money can actually do.
            </p>
            <div className={`mt-5 max-w-xl space-y-1 ${body}`}>
              <p>So you can spend less time reconstructing the decision.</p>
              <p>And more time making it.</p>
            </div>
            <p className="mt-6 font-serif text-2xl leading-snug text-[#164C3B]">
              The calculations? Handled.<br />The final decision? Yours.
            </p>
            <div className="mt-9 flex w-full max-w-[460px] flex-col items-start gap-4">
              <OfferCard testid="hero-purchase-button" align="left" />
              <button type="button" onClick={() => scrollTo("how-it-works")} data-testid="hero-how-button"
                className="mt-1 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#2F6B57] transition-colors hover:text-[#164C3B]">
                See what an Edit feels like <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="scale-in w-full lg:justify-self-center lg:pt-10"><HeroDashboard /></div>
        </div>
      </section>

      {/* SECTION 2 - RECOGNITION */}
      <section className="border-t border-[#ECE6DC] bg-white">
        <div className="mx-auto w-full max-w-[880px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>The hard part wasn't getting paid.</h2>
          <div className={`mt-7 space-y-4 ${body}`}>
            <p>You got paid.</p>
            <p>Lovely.</p>
            <p>Then somehow your income immediately became a group project.</p>
          </div>
          <div className="mt-6 space-y-2 border-l-2 border-[#DDE7DF] pl-5 text-lg text-[#171717]">
            <p>Mortgage wants something.</p>
            <p>Debt wants something.</p>
            <p>Savings would also like a word.</p>
            <p>Groceries have entered the chat.</p>
          </div>
          <p className={`mt-6 ${body}`}>And that bill you absolutely remembered until approximately six minutes ago has impeccable timing.</p>
          <div className="mt-6 space-y-1.5 text-[17px] text-[#525252]">
            <p>So you check the numbers.</p>
            <p>Calculate what's left.</p>
            <p>Remember something else.</p>
            <p>Calculate again.</p>
            <p>Consider one decision.</p>
            <p>Second-guess it.</p>
            <p>Check another number.</p>
            <p>And eventually think:</p>
          </div>
          <p className={`mt-8 text-2xl sm:text-3xl ${H}`}>“I make money. Why does managing it still require this much of me?”</p>
          <div className={`mt-7 space-y-4 ${body}`}>
            <p>There it is.</p>
            <p>Because maybe the problem isn't that you don't understand money.</p>
            <p>Maybe you're simply tired of <strong className="font-semibold text-[#171717]">reconstructing the decision every time something changes.</strong></p>
          </div>
        </div>
      </section>

      {/* SECTION 3 - THE REAL PROBLEM */}
      <section className="bg-[#FAF8F4]">
        <div className="mx-auto w-full max-w-[920px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.5rem] ${H}`}>You wanted to get better with money.</h2>
          <h2 className={`mt-3 text-3xl sm:text-[2.5rem] ${H}`}>You didn't mean you wanted money management to become your part-time job.</h2>
          <div className={`mt-8 space-y-4 ${body}`}>
            <p>A calculator can do the math.</p>
            <p className="font-semibold text-[#171717]">That wasn't the problem.</p>
            <p>A spreadsheet can organize the numbers.</p>
            <p>That wasn't quite the problem either.</p>
            <p>The friction starts when everything wants something from the same money.</p>
          </div>
          <div className="mt-6 space-y-2 border-l-2 border-[#DDE7DF] pl-5 text-lg text-[#171717]">
            <p>Something needs protecting.</p>
            <p>Something needs paying.</p>
            <p>Something needs building.</p>
            <p>Real life would also like to submit a request.</p>
          </div>
          <div className={`mt-6 space-y-1 ${body}`}>
            <p>And you're standing in the middle deciding who gets what.</p>
            <p>Again.</p>
          </div>
          <h2 className={`mt-10 text-3xl sm:text-[2.7rem] ${H}`}>You've become the referee.</h2>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>Not because you're bad with money.</p>
            <p>Because financial decisions have competing priorities.</p>
            <p>And every time the picture changes, you're back at the table sorting through them.</p>
          </div>
          <p className="mt-7 font-serif text-2xl leading-snug text-[#164C3B]">The Paycheck Edit was built to reduce that work.</p>
        </div>
      </section>

      <OfferBand testid="after-problem-purchase-button" bg="bg-[#FAF8F4]" />

      {/* SECTION 4 - PRODUCT REVEAL (signature dark) */}
      <section id="product-reveal" className="scroll-mt-16 bg-[#164C3B]">
        <div className="mx-auto w-full max-w-[920px] px-5 py-16 sm:px-8 lg:py-28">
          <div className="space-y-1">
            <h2 className={`text-4xl sm:text-6xl ${Hd}`}>Get paid.</h2>
            <h2 className={`text-4xl text-[#C9A86A] sm:text-6xl ${Hd} !text-[#C9A86A]`}>Make an Edit.</h2>
            <h2 className={`text-4xl sm:text-6xl ${Hd}`}>Decide.</h2>
          </div>
          <p className="mt-7 text-lg font-medium text-[#DDE7DF]">That's the rhythm.</p>
          <div className={`mt-6 space-y-4 ${bodyLight}`}>
            <p>You provide the financial picture that matters.</p>
            <p className="font-semibold text-white">The Paycheck Edit uses the financial picture you provide to calculate your Edit.</p>
            <p>Then it brings the decision back to you.</p>
          </div>
          <div className="mt-8 space-y-3">
            {[["Want to understand why?", "You can."], ["Want to see the math?", "It's there."], ["Want to decide differently?", null]].map(([q, a]) => (
              <div key={q} className="flex flex-wrap items-baseline gap-x-3">
                <span className="text-lg font-medium text-white">{q}</span>
                {a && <span className="text-lg text-[#C9A86A]">{a}</span>}
              </div>
            ))}
          </div>
          <h3 className={`mt-8 text-3xl sm:text-4xl ${Hd}`}>It's still your money.</h3>
          <div className={`mt-6 space-y-3 ${bodyLight}`}>
            <p>No software moving it for you.</p>
            <p>No recommendation quietly becoming a decision.</p>
            <p>No surrendering control because an algorithm put something in a pretty box.</p>
          </div>
          <p className="mt-7 font-serif text-2xl leading-snug text-white">
            The software calculates.<br /><span className="text-[#C9A86A]">You decide.</span>
          </p>
          <div className="mt-10"><OfferCard testid="reveal-purchase-button" tone="dark" align="left" /></div>
        </div>
      </section>

      {/* SECTION 5 - THE DIFFERENCE */}
      <section className="bg-white">
        <div className="mx-auto w-full max-w-[880px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>We're not tracking your $6 latte.</h2>
          <p className="mt-5 font-serif text-xl italic text-[#2F6B57]">Because that's not the assignment.</p>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>The Paycheck Edit isn't trying to know everything you bought today.</p>
            <p>It doesn't connect to your bank.</p>
            <p>It doesn't monitor your transactions.</p>
            <p>And it doesn't require an elaborate category system before it becomes useful.</p>
            <p>Because there's another financial problem worth solving:</p>
          </div>
          <p className="mt-7 font-serif text-2xl leading-snug text-[#164C3B]">
            The repeated work between having money available and deciding what you want it to do.
          </p>

          <div className="mt-9 overflow-hidden rounded-3xl pe-surface" data-testid="no-bank-connection">
            <div className="border-b border-[#EFEBE3] bg-[#FCFBF8] px-6 py-5 sm:px-8">
              <p className="pe-eyebrow text-[#2F6B57]">The simpler part</p>
              <h3 className={`mt-2 text-2xl sm:text-[1.7rem] ${H}`}>No bank connection required.</h3>
            </div>
            <div className="grid gap-x-8 gap-y-4 px-6 py-7 sm:grid-cols-2 sm:px-8">
              {[
                "No bank login required.",
                "No account numbers or financial logins needed.",
                "No transaction monitoring.",
                "No automatic money movement.",
                "No categorizing every purchase.",
                "You choose the financial information you enter.",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                  </span>
                  <span className="text-[16px] leading-snug text-[#171717]">{item}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#EFEBE3] px-6 py-6 sm:px-8">
              <p className="text-[16px] leading-relaxed text-[#525252]">
                The Paycheck Edit does not need to monitor every transaction to help you make your next money decision. You enter the information used for your calculations, and you decide what to do with the results.
              </p>
              <p className="mt-4 text-sm font-semibold uppercase tracking-[0.16em] text-[#B8923F]">Less tracking. More intentional action.</p>
            </div>
          </div>

          <div className={`mt-7 space-y-3 ${body}`}>
            <p>A calculator can calculate.</p>
            <p>A spreadsheet can organize.</p>
            <p>And yes, you could piece the entire decision together yourself.</p>
            <p>In fact...</p>
          </div>
          <h3 className={`mt-8 text-3xl sm:text-4xl ${H}`}>That's exactly how this product started.</h3>
          <p className={`mt-5 ${body}`}>We'll come back to that.</p>
        </div>
      </section>

      <OfferBand testid="after-difference-purchase-button" />

      {/* SECTION 6 - PREMIUM BENEFIT */}
      <section className="bg-[#FAF8F4]">
        <div className="mx-auto w-full max-w-[920px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>Consider the tedious part handled.</h2>
          <div className={`mt-7 space-y-3 ${body}`}>
            <p>Imagine getting paid without immediately opening your mental financial filing cabinet.</p>
            <p>Without reconstructing the same decision.</p>
            <p>Without wondering whether you forgot something.</p>
            <p>Without turning one financial decision into a small research project.</p>
          </div>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>The Paycheck Edit isn't designed to make you think about money <strong className="font-semibold text-[#171717]">more</strong>.</p>
            <p className="font-serif text-2xl leading-snug text-[#164C3B]">It's designed to make the thinking you already have to do more useful.</p>
          </div>
          <div className={`mt-7 space-y-2 ${body}`}>
            <p>Your financial priorities can extend beyond one bill or one goal.</p>
            <p>The system is built around four ideas:</p>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="decision-four">
            {["Protect", "Eliminate", "Build", "Freedom"].map((w, i) => (
              <span key={w} className="flex items-center gap-5">
                <span className="font-serif text-2xl text-[#164C3B] sm:text-3xl">{w}</span>
                {i < 3 && <span className="text-[#C9A86A]">·</span>}
              </span>
            ))}
          </div>
          <div className={`mt-7 space-y-2 ${body}`}>
            <p>There's more underneath those four words.</p>
            <p>That's where we're going to leave it.</p>
            <p>What matters before you buy is this:</p>
          </div>
          <h3 className={`mt-8 text-2xl sm:text-[2rem] ${H}`}>Your money can have competing priorities without you having to manually referee all of them.</h3>
          <div className={`mt-7 space-y-2 ${body}`}>
            <p>You bring the financial reality.</p>
            <p>The Paycheck Edit brings the structure.</p>
            <p className="font-semibold text-[#171717]">You keep the authority.</p>
          </div>
        </div>
      </section>

      {/* SECTION 6.5 - PRODUCT LOOP DEMO */}
      <section id="how-it-works" className="scroll-mt-16 border-t border-[#ECE6DC] bg-[#FAF8F4]">
        <div className="mx-auto w-full max-w-[920px] px-5 py-16 sm:px-8 lg:py-24">
          <p className="pe-eyebrow">How it works</p>
          <h2 className={`mt-3 text-3xl sm:text-[2.7rem] ${H}`}>See what The Paycheck Edit actually does.</h2>
          <p className={`mt-5 max-w-xl ${body}`}>One paycheck, from landing to decision. Example numbers shown for illustration.</p>

          <div className="mt-10 space-y-2" data-testid="edit-demo">
            <div className="rounded-3xl pe-surface p-6 sm:p-7" data-testid="edit-step-landed">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-[#525252]"><Coins className="h-4 w-4 text-[#164C3B]" /> Feeling rich?</span>
                <span className="rounded-full bg-[#DDE7DF] px-2.5 py-0.5 text-[11px] font-medium text-[#164C3B]">Illustration</span>
              </div>
              <p className="mt-2 font-serif text-4xl text-[#171717] sm:text-5xl">$5,487</p>
            </div>

            <div className="flex justify-center py-1"><ArrowRight className="h-5 w-5 rotate-90 text-[#C9A86A]" /></div>

            <div className="rounded-3xl pe-surface p-6 sm:p-7" data-testid="edit-step-cover">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#164C3B] text-[13px] font-semibold text-[#C9A86A]">1</span>
                <p className="pe-eyebrow text-[#2F6B57]">Cover what matters</p>
              </div>
              <p className="mt-3 text-[17px] leading-relaxed text-[#171717]">Account for the financial foundation you've already established.</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["Bills", "Essentials", "Runway"].map((t) => (
                  <span key={t} className="rounded-full bg-[#DDE7DF] px-2.5 py-0.5 text-[11px] font-medium text-[#164C3B]">{t}</span>
                ))}
              </div>
            </div>

            <div className="flex justify-center py-1"><ArrowRight className="h-5 w-5 rotate-90 text-[#C9A86A]" /></div>

            <div className="rounded-3xl bg-[#164C3B] p-6 text-white sm:p-7" data-testid="edit-step-available">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#C9A86A] text-[13px] font-semibold text-[#164C3B]">2</span>
                <p className="text-[11px] uppercase tracking-[0.14em] text-[#BFD6C9]">See what's actually available</p>
              </div>
              <p className="mt-3 font-serif text-4xl text-[#C9A86A] sm:text-5xl">You can edit $1,840</p>
            </div>

            <div className="flex justify-center py-1"><ArrowRight className="h-5 w-5 rotate-90 text-[#C9A86A]" /></div>

            <div className="rounded-3xl pe-surface p-6 sm:p-7" data-testid="edit-step-move">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#164C3B] text-[13px] font-semibold text-[#C9A86A]">3</span>
                <p className="pe-eyebrow text-[#2F6B57]">See the move</p>
              </div>
              <p className="mt-3 text-[17px] leading-relaxed text-[#171717]">See how the available amount could be applied, based on the priority you've set.</p>
              <div className="mt-3 flex items-center justify-between rounded-xl border border-[#C9A86A]/50 bg-[#F9F5EC] px-4 py-3">
                <span className="inline-flex items-center gap-2 font-serif text-base text-[#164C3B]"><Target className="h-4 w-4 text-[#2F6B57]" /> $1,500 to Visa</span>
                <span className="rounded-full bg-[#2F6B57] px-2.5 py-1 text-[11px] font-semibold text-white">Balance drops</span>
              </div>
            </div>

            <div className="flex justify-center py-1"><ArrowRight className="h-5 w-5 rotate-90 text-[#C9A86A]" /></div>

            <div className="rounded-3xl pe-surface p-6 sm:p-7" data-testid="edit-step-why">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#164C3B] text-[13px] font-semibold text-[#C9A86A]">4</span>
                <p className="pe-eyebrow text-[#2F6B57]">Understand why</p>
              </div>
              <p className="mt-3 text-[17px] leading-relaxed text-[#171717]">See the reasoning behind the Edit, and inspect the math supporting it.</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["Why this move", "See the math"].map((t) => (
                  <span key={t} className="rounded-full border border-[#C9A86A]/50 bg-[#F9F5EC] px-2.5 py-0.5 text-[11px] font-medium text-[#8A6E2F]">{t}</span>
                ))}
              </div>
            </div>

            <div className="flex justify-center py-1"><ArrowRight className="h-5 w-5 rotate-90 text-[#C9A86A]" /></div>

            <div className="rounded-3xl pe-surface p-6 sm:p-7" data-testid="edit-step-decide">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#164C3B] text-[13px] font-semibold text-[#C9A86A]">5</span>
                  <p className="pe-eyebrow text-[#2F6B57]">You decide</p>
                </div>
                <ShieldCheck className="h-4 w-4 text-[#9A7B3A]" />
              </div>
              <p className="mt-3 font-serif text-2xl leading-snug text-[#164C3B]">Nothing moves unless you move it.</p>
            </div>
          </div>

          <p className="mt-6 text-xs italic leading-relaxed text-[#8A7C6B]">
            Illustration with example numbers. You confirm every payment yourself. The Paycheck Edit never moves your money.
          </p>
        </div>
      </section>

      <OfferBand testid="after-how-purchase-button" />

      {/* SECTION 7 - IDENTITY */}
      <section className="border-t border-[#ECE6DC] bg-white">
        <div className="mx-auto w-full max-w-[880px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>Your money deserves your attention.</h2>
          <h2 className={`mt-2 text-3xl text-[#2F6B57] sm:text-[2.7rem] ${H} !text-[#2F6B57]`}>Not all of your attention.</h2>
          <div className={`mt-7 space-y-4 ${body}`}>
            <p>There is a version of being “good with money” that seems to require thinking about money constantly.</p>
            <p>This isn't that.</p>
            <p>You don't need another financial system competing for your attention.</p>
            <p>You need one that earns its place by reducing the amount of checking, calculating, remembering, updating and reconsidering required from you.</p>
          </div>
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-1.5 text-base font-medium text-[#164C3B]">
            <span>Get paid.</span><span>Make an Edit.</span><span>Understand the decision.</span><span>Make the call.</span><span>Get on with your life.</span>
          </div>
          <h3 className={`mt-9 text-2xl sm:text-3xl ${H}`}>Composed. Informed. Intentional.</h3>
          <div className={`mt-6 space-y-2 ${body}`}>
            <p>Not because money suddenly became simple.</p>
            <p>Because you stopped making yourself rebuild every decision from scratch.</p>
          </div>
        </div>
      </section>

      {/* SECTION 8 - REAL LIFE */}
      <section className="bg-[#FAF8F4]">
        <div className="mx-auto w-full max-w-[880px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>And then life life-ed. <span aria-hidden="true">😑</span></h2>
          <div className={`mt-7 space-y-4 ${body}`}>
            <p>Of course it did.</p>
            <p>The beautifully considered decision you made on Monday has never stopped Thursday from having ideas.</p>
          </div>
          <div className="mt-6 space-y-2 border-l-2 border-[#DDE7DF] pl-5 text-lg text-[#171717]">
            <p>Income changes.</p>
            <p>An expense shows up.</p>
            <p>Priorities shift.</p>
            <p>You decide differently.</p>
            <p>Sometimes you pause.</p>
            <p>Sometimes you come back.</p>
          </div>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>The Paycheck Edit was built knowing real people would use it in real financial lives.</p>
            <p>So when reality changes, the answer isn't:</p>
            <p className="font-semibold text-[#171717]">“Well. You ruined the plan.”</p>
            <p>It's:</p>
          </div>
          <h3 className={`mt-6 text-3xl sm:text-4xl ${H}`}>Okay. What's true now?</h3>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-1.5 text-base font-medium text-[#164C3B]">
            <span>Adjust.</span><span>Reconsider.</span><span>Continue.</span>
          </div>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>And if there isn't money available for another decision right now?</p>
            <p>That's information too.</p>
            <p>The software doesn't need to manufacture progress to keep you clicking.</p>
          </div>
          <p className="mt-7 font-serif text-2xl leading-snug text-[#164C3B]">A useful money system shouldn't require a perfectly behaved life.</p>
        </div>
      </section>

      {/* SECTION 9 - TRUST */}
      <section className="border-t border-[#ECE6DC] bg-white">
        <div className="mx-auto w-full max-w-[880px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.6rem] ${H}`}>“Okay. But why that?”</h2>
          <div className={`mt-6 space-y-4 ${body}`}>
            <p>Exactly.</p>
            <p>You shouldn't have to blindly trust financial software because it produced a confident-looking number.</p>
          </div>
          <div className="mt-7 space-y-5">
            <div data-testid="why-this-move">
              <p className="pe-eyebrow">Why this move</p>
              <p className={`mt-1.5 ${body}`}>lets you understand the reasoning behind an Edit.</p>
            </div>
            <div data-testid="see-the-math">
              <p className="pe-eyebrow">See the math</p>
              <p className={`mt-1.5 ${body}`}>lets you inspect the numbers supporting it.</p>
            </div>
          </div>
          <div className={`mt-7 space-y-3 ${body}`}>
            <p>And when you're considering possibilities, hypothetical doesn't quietly become history.</p>
            <p>Because The Paycheck Edit distinguishes between something you considered and something you actually confirmed.</p>
          </div>
          <h3 className={`mt-8 text-2xl sm:text-[2rem] ${H}`}>The software doesn't get credit for a decision you never made.</h3>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>Neither should your progress.</p>
            <p>That's why the final authority stays exactly where it belongs.</p>
          </div>
          <p className="mt-6 font-serif text-4xl text-[#164C3B]">With you.</p>
        </div>
      </section>

      {/* SECTION 10 - FOUNDER STORY */}
      <section id="about" className="scroll-mt-16 bg-[#FAF8F4]">
        <div className="mx-auto w-full max-w-[860px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.6rem] ${H}`}>I know you could do this yourself.</h2>
          <h2 className={`mt-2 text-3xl text-[#2F6B57] sm:text-[2.6rem] ${H} !text-[#2F6B57]`}>I did.</h2>
          <p className={`mt-7 ${body}`}>Before The Paycheck Edit was software, I was working through the underlying approach manually with my own money.</p>
          <div className="mt-6 rounded-3xl pe-surface p-7 sm:p-9">
            <p className="font-serif text-2xl leading-snug text-[#171717] sm:text-[1.7rem]">
              Using the underlying approach, I paid off approximately $32,000 in consumer debt in roughly four months on one income.
            </p>
            <p className="mt-4 font-semibold text-[#2F6B57]">That's my personal historical experience, not a promise of what the product will do for you.</p>
          </div>
          <div className={`mt-7 space-y-3 ${body}`}>
            <p>But eventually I realized the result wasn't the whole story.</p>
            <p>The amount of manual work behind those decisions was a story too.</p>
          </div>
          <div className="mt-5 space-y-1.5 text-[17px] text-[#525252]">
            <p>Checking.</p><p>Calculating.</p><p>Recalculating.</p><p>Accounting for what changed.</p>
            <p>Working through competing priorities.</p><p>Deciding.</p><p>Repeating.</p>
          </div>
          <h3 className={`mt-8 text-3xl sm:text-4xl ${H}`}>Doing this manually was rude.</h3>
          <div className={`mt-6 space-y-3 ${body}`}>
            <p>And once you've developed a process you trust, doing all the administrative work yourself stops feeling particularly impressive.</p>
            <p>It starts feeling inefficient.</p>
            <p>So I built what I wanted:</p>
          </div>
          <h3 className={`mt-7 text-2xl sm:text-[2rem] ${H}`}>A system that could handle more of the tedious work behind the decision without taking the decision away from me.</h3>
          <p className={`mt-6 ${body}`}>That's The Paycheck Edit.</p>
          <p className="mt-7 text-sm italic leading-relaxed text-[#8A7C6B]" data-testid="founder-result-disclosure">
            This is the founder's historical personal experience, not a representation or guarantee of customer results. Individual circumstances and results vary. The Paycheck Edit™ does not guarantee debt payoff, savings, wealth, investment performance or other financial outcomes.
          </p>
        </div>
      </section>

      <OfferBand testid="after-founder-purchase-button" />

      {/* SECTION 11 - THE RECEIPTS */}
      <section id="receipts" className="scroll-mt-16 border-t border-[#ECE6DC] bg-white">
        <div className="mx-auto w-full max-w-[860px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>Considered isn't completed.</h2>
          <div className={`mt-7 space-y-3 ${body}`}>
            <p>Recommendations are useful.</p>
            <p>Possibilities are useful.</p>
            <p>Planning is useful.</p>
            <p>But none of those are the same as something you actually decided to do.</p>
            <p>The Paycheck Edit respects the difference.</p>
          </div>
          <div className="mt-7">
            <p className="pe-eyebrow">The Receipts</p>
            <p className={`mt-1.5 ${body}`}>gives confirmed decisions somewhere to tell their story.</p>
          </div>
          <h3 className={`mt-8 text-2xl sm:text-[2rem] ${H}`}>Confirmed, not hypothetical.</h3>
          <p className={`mt-6 ${body}`}>Because your progress shouldn't include things the software suggested but you never acted on.</p>
          <p className={`mt-5 ${body}`}>Eventually, that creates a different conversation with your money.</p>
          <div className="mt-6 space-y-2">
            <p className={body}>Less:</p>
            <p className="font-serif text-xl italic text-[#171717]">“What have I even been doing with all of this?”</p>
            <p className={`mt-3 ${body}`}>More:</p>
            <p className="font-serif text-3xl text-[#164C3B]">“Oh. There it is.”</p>
          </div>
        </div>
      </section>

      {/* SECTION 12 - OFFER */}
      <section id="pricing" className="scroll-mt-16 bg-[#FAF8F4]">
        <div className="mx-auto w-full max-w-[880px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-[2.7rem] ${H}`}>Yes. You could keep doing it manually.</h2>
          <div className="mt-7 space-y-2 border-l-2 border-[#DDE7DF] pl-5 text-lg text-[#171717]">
            <p>You could keep the spreadsheet.</p>
            <p>Keep the calculator nearby.</p>
            <p>Keep remembering what changed.</p>
            <p>Keep reconstructing the picture.</p>
            <p>Keep revisiting previous decisions.</p>
            <p>Keep working through competing priorities.</p>
            <p>Keep checking the math because the mental math suddenly looked suspicious.</p>
          </div>
          <p className={`mt-6 ${body}`}>You are fully capable.</p>
          <h3 className={`mt-9 text-3xl sm:text-4xl ${H}`}>That's not the question.</h3>
          <h3 className={`mt-2 text-3xl text-[#2F6B57] sm:text-4xl ${H} !text-[#2F6B57]`}>Why are you still doing all of that yourself?</h3>
          <div className="mt-9 rounded-[1.75rem] border border-[#C9A86A]/40 bg-[#FCFBF8] p-7 shadow-[0_26px_64px_-34px_rgba(22,76,59,0.3)] sm:p-9" data-testid="price-justification">
            <p className={`text-2xl leading-snug sm:text-[2rem] ${H}`}>You're not paying $147 because you can't calculate.</p>
            <p className="mt-4 font-serif text-xl leading-relaxed text-[#2F6B57] sm:text-2xl">You're paying so you don't have to keep reconstructing the same financial decision yourself.</p>
          </div>
          <p className={`mt-7 ${body}`}>You're paying for a system designed to reduce how much financial administration sits between:</p>
          <div className="mt-6 space-y-1">
            <p className={`text-2xl sm:text-3xl ${H}`}>I got paid.</p>
            <p className={`${body}`}>and</p>
            <p className={`text-2xl sm:text-3xl ${H}`}>I know what I want to do next.</p>
          </div>
          <p className="mt-8 text-[17px] text-[#171717]">The Paycheck Edit™ is <strong className="font-semibold">$147 once.</strong></p>
          <div className="mt-4 space-y-1.5 text-lg text-[#525252]">
            <p>No subscription.</p>
            <p>No bank connection required.</p>
            <p>No automatic money movement.</p>
            <p>No transaction babysitting.</p>
            <p>No need to turn money management into a hobby.</p>
          </div>
          <div className="mt-10">
            <OfferCard testid="pricing-purchase-button" caption="The one-time offer"
              priceLabel="ONE-TIME PURCHASE" note="Secure checkout · Digital access after purchase" align="left">
              <div className="w-full">
                <p className="font-serif text-2xl text-[#171717]">The Paycheck Edit™</p>
                <p className="mt-1 font-serif text-6xl text-[#164C3B]" data-testid="price-amount">$147<span className="ml-2 align-middle text-base font-normal text-[#70584A]">one-time</span></p>
                <ul className="mt-5 space-y-2 text-[15px] text-[#3f3f3f]" data-testid="offer-feature-list">
                  {[
                    "Full core planning system",
                    "Ongoing Paycheck Edits",
                    "Protect, Eliminate, Build & Freedom planning",
                    "Debt, protection and goal planning",
                    "What If scenarios",
                    "The Receipts and confirmed progress tracking",
                    "Ongoing access",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </OfferCard>
          </div>
          <div className="mt-6 rounded-2xl border border-[#E5E0D9] bg-[#FBF9F4] p-5" data-testid="guarantee-block">
            <p className="flex items-center gap-2 font-serif text-xl text-[#164C3B]"><ShieldCheck className="h-5 w-5" /> 14-Day Money-Back Guarantee</p>
            <p className="mt-2 text-[15px] leading-relaxed text-[#525252]">
              Try The Paycheck Edit™ for 14 days. If it is not the right fit, email{" "}
              <a href="mailto:hello@thepaycheckedit.co" className="font-medium text-[#2F6B57] hover:underline">hello@thepaycheckedit.co</a>{" "}
              within 14 days of your successful purchase for an eligible refund.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-[#70584A]">
              Your 14-day period begins when your payment is successfully charged. Refunds are returned to the original payment method where reasonably possible. Your applicable consumer rights are not affected.
            </p>
            <a href="/legal#refund" className="mt-3 inline-block text-sm font-medium text-[#2F6B57] hover:underline" data-testid="guarantee-policy-link">Read the full 14-Day Money-Back Guarantee</a>
          </div>
          <p className="mt-4 text-sm text-[#8A7C6B]" data-testid="offer-us-only">Currently available to customers in the United States only.</p>
        </div>
      </section>
      <section id="faq" className="scroll-mt-16 border-t border-[#ECE6DC] bg-white">
        <div className="mx-auto w-full max-w-[820px] px-5 py-16 sm:px-8 lg:py-24">
          <h2 className={`text-3xl sm:text-4xl ${H}`}>The questions worth asking</h2>
          <div className="mt-10 space-y-3" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <FaqRow key={i} idx={i} f={f} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
          <p className="mt-12 text-sm font-semibold uppercase tracking-[0.18em] text-[#B8923F]">A few practical things</p>
          <div className="mt-5 space-y-3" data-testid="faq-practical">
            {PRACTICAL.map((f, i) => {
              const idx = FAQS.length + i;
              return <FaqRow key={idx} idx={idx} f={f} open={openFaq === idx} onToggle={() => setOpenFaq(openFaq === idx ? -1 : idx)} />;
            })}
          </div>
        </div>
      </section>

      {/* SECTION 14 - FINAL CLOSE (dark) */}
      <section className="bg-[#164C3B]">
        <div className="mx-auto w-full max-w-[880px] px-5 py-20 sm:px-8 lg:py-28">
          <h2 className={`text-3xl sm:text-[2.7rem] ${Hd}`}>Your income graces your account.</h2>
          <div className={`mt-7 space-y-4 ${bodyLight}`}>
            <p>You already did the work to earn it.</p>
            <p>You don't need another round of checking, calculating, remembering and rebuilding just to decide what happens next.</p>
          </div>
          <h3 className={`mt-9 text-2xl sm:text-[2rem] ${Hd}`}>Let The Paycheck Edit handle the tedious part.</h3>
          <h3 className={`mt-2 text-2xl text-[#C9A86A] sm:text-[2rem] ${Hd} !text-[#C9A86A]`}>Keep the decision.</h3>
          <h3 className={`mt-6 text-4xl sm:text-5xl ${Hd}`}>Make an Edit.</h3>
          <div className="mt-10"><OfferCard testid="final-purchase-button" tone="dark" align="left" /></div>
          <p className="mt-10 text-xs italic leading-relaxed text-[#9FBBAE]" data-testid="final-disclaimer">
            The Paycheck Edit™ provides educational financial planning and decision-support tools based on information you provide. It does not provide financial, investment, tax, accounting or legal advice. You remain responsible for your financial decisions. Individual circumstances and results vary.
          </p>
        </div>
      </section>

      <StickyCTA />
      <JourneyFooter disclaimerTo="/legal" />
    </div>
  );
}

function FaqRow({ idx, f, open, onToggle }) {
  return (
    <div className="overflow-hidden rounded-2xl pe-surface">
      <button type="button" onClick={onToggle} data-testid={`faq-question-${idx}`}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
        <span className="text-[16px] font-semibold text-[#171717]">{f.q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#2F6B57] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6" data-testid={`faq-answer-${idx}`}>
          <p className="text-[15px] leading-relaxed text-[#525252]">{f.a}</p>
          {f.bold && <p className="mt-3 font-semibold text-[#171717]">{f.bold}</p>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Product preview - representative product UI in the real visual
// language (ivory/sage/deep green/champagne, serif numbers). Explicitly
// labeled as an illustration; never presented as a customer result.
// ---------------------------------------------------------------------
const SCENARIOS = [
  { name: "Protect", kind: "protect", landed: "$1,850", reserved: "$1,850",
    potential: "$0", moveLabel: "Protected Runway", chip: "Runway grows", note: "Protection is progress, too." },
  { name: "Eliminate", kind: "debt", landed: "$3,200", reserved: "$2,450",
    potential: "$750", moveLabel: "$750 to Visa", chip: "Balance drops", note: "Projected balance: $1,200" },
  { name: "Rollover", kind: "debt", landed: "$5,800", reserved: "$3,400",
    potential: "$2,400", moveLabel: "$1,500 clears Visa", chip: "Debt eliminated",
    rollover: "$900 rolls to Capital One", note: "Momentum carries to the next debt." },
];

function HeroDashboard() {
  const ref = useRef(null);
  const reduce = typeof window !== "undefined" && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(reduce ? 2 : 0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => setVisible(entries[0].isIntersecting), { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  useEffect(() => {
    if (reduce || !visible) return;
    const HOLD_MS = 3000, FADE_MS = 520;
    const timers = [];
    const cycle = () => {
      timers.push(setTimeout(() => {
        setShow(false);
        timers.push(setTimeout(() => { setIdx((i) => (i + 1) % SCENARIOS.length); setShow(true); cycle(); }, FADE_MS));
      }, HOLD_MS));
    };
    cycle();
    return () => timers.forEach(clearTimeout);
  }, [reduce, visible]);

  const s = SCENARIOS[idx];
  const val = {
    transition: "opacity 460ms ease, transform 460ms cubic-bezier(0.22,1,0.36,1)",
    opacity: show ? 1 : 0.5,
    transform: show ? "translateY(0)" : "translateY(5px)",
  };
  const isProtect = s.kind === "protect";

  return (
    <div ref={ref} data-testid="hero-dashboard" className="mx-auto w-full max-w-[500px] overflow-hidden rounded-3xl pe-surface shadow-[0_30px_60px_-30px_rgba(22,76,59,0.24)]">
      <div className="flex items-center gap-2 border-b border-[#EFEBE3] bg-[#FCFBF8] px-5 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E5E0D9]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E5E0D9]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E5E0D9]" />
        <span className="ml-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7C6B]">The Paycheck Edit</span>
        <span data-testid="hero-scenario-name" style={val} className="ml-auto text-[11px] font-medium text-[#A89A88]">{s.name}</span>
      </div>
      <div className="space-y-3 p-5">
        <div className="flex items-center justify-between rounded-xl bg-[#FCFBF8] px-4 py-3">
          <span className="inline-flex items-center gap-2 text-sm text-[#525252]"><Coins className="h-4 w-4 text-[#164C3B]" /> Feeling rich?</span>
          <span data-testid="hero-landed" style={val} className="font-serif text-xl text-[#171717]">{s.landed}</span>
        </div>
        <div className="rounded-xl border border-[#ECE6DC] px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm text-[#164C3B]"><ShieldCheck className="h-4 w-4" /> Protected first</span>
            <span data-testid="hero-reserved" style={val} className="text-sm font-semibold text-[#8B6B2B]">reserved {s.reserved}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {["Bills", "Essentials", "Runway"].map((t) => (
              <span key={t} className="rounded-full bg-[#DDE7DF] px-2.5 py-0.5 text-[11px] font-medium text-[#164C3B]">{t}</span>
            ))}
          </div>
        </div>
        <div className="flex justify-center"><ArrowRight className="h-4 w-4 rotate-90 text-[#C9A86A]" /></div>
        <div className="rounded-2xl bg-[#164C3B] p-5 text-white">
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#BFD6C9]">Available for this Edit</p>
          <p data-testid="hero-potential" style={val} className="mt-1 font-serif text-4xl text-[#C9A86A]">{s.potential}</p>
        </div>
        <div style={val} className={`rounded-xl border px-4 py-3 ${isProtect ? "border-[#C9A86A]/60 bg-[#FBF7EF]" : "border-[#C9A86A]/50 bg-[#F9F5EC]"}`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7C6B]">{isProtect ? "Your move this cycle" : "Your proposed Edit"}</p>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#171717]">
              {isProtect ? <ShieldCheck className="h-4 w-4 text-[#9A7B3A]" /> : <Target className="h-4 w-4 text-[#2F6B57]" />}
              <span className="font-serif text-base text-[#164C3B]">{s.moveLabel}</span>
            </span>
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold text-white ${isProtect ? "bg-[#C9A86A]" : "bg-[#2F6B57]"}`}>{s.chip}</span>
          </div>
          <p className="mt-1.5 pl-6 text-xs text-[#525252]">{s.note}</p>
          {s.rollover && (
            <div className="mt-2 flex items-center gap-2 border-t border-[#EADFC7] pt-2 text-xs text-[#2F6B57]">
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[#C9A86A]" /><span className="font-medium">{s.rollover}</span>
            </div>
          )}
        </div>
      </div>
      <p className="border-t border-[#EFEBE3] px-5 py-3 text-center text-[11px] leading-relaxed text-[#8A7C6B]">
        Illustration. You confirm every payment yourself. The Paycheck Edit never moves your money.
      </p>
    </div>
  );
}
